import PropTypes from 'prop-types';
import Flux from './../../flux/Flux';
import Translations from './../../utils/Translations';
import { Component } from 'react';

const MSECS_TO_HIDE_TOOLTIP = 3000;
const HIDE_TOOLTIP_ANIMATION_DURATION_MSECS = 330;

//! taken from https://stackoverflow.com/questions/400212/how-do-i-copy-to-the-clipboard-in-javascript
function fallbackCopyTextToClipboard() {
	const textArea = document.querySelector('.social-share-bar__textarea');
	if (!textArea) {
		return;
	}

	textArea.focus();
	textArea.select();

	document.execCommand('copy');
}

/**
 * @param {string} text
 */
function copyTextToClipboard(text) {
	if (!navigator.clipboard) {
		fallbackCopyTextToClipboard();
		return;
	}
	navigator.clipboard.writeText(text);
}

class SocialShareBarShareContentUrl extends Component {
	constructor(props) {
		super(props);

		this.state = {
			currentVideoTime: 0,
			urlWithTimestamp: false,
			tooltipVisible: false,
		};

		this.shareTextFieldRef = null;
		this.tooltipHandle = null;

		this.getCurrentVideoTime = this.getCurrentVideoTime.bind(this);
		this.onChangeCheckbox = this.onChangeCheckbox.bind(this);
		this.selectUrlForCopy = this.selectUrlForCopy.bind(this);
		this.onShowTooltipButton = this.onShowTooltipButton.bind(this);

		Flux.Gallery.addCurrentVideoTimeChangeListener(this.getCurrentVideoTime);
	}

	componentDidUpdate() {
		this.selectUrlForCopy();
	}

	componentWillUnmount() {
		Flux.Gallery.removeCurrentVideoTimeChangeListener(this.getCurrentVideoTime);
		clearTimeout(this.tooltipHandle);
		this.props.copyLinkTooltipChanged(false);
	}

	generateUrlWithTimestamp() {
		const newVideoUrl = this.props.videoUrl.replace(/(\?videotimestamp=)([0-9])+/g, ''); //To remove the duplicated '?videotimestamp='
		return `${newVideoUrl}?videotimestamp=${this.state.currentVideoTime}`;
	}

	getCurrentVideoTime() {
		let currentVideoTime = Flux.Gallery.getCurrentVideoTime();
		currentVideoTime = Math.round(currentVideoTime);
		this.setState({ currentVideoTime: currentVideoTime });
	}

	onChangeCheckbox(event) {
		this.setState({ urlWithTimestamp: event.target.checked });
	}

	selectUrlForCopy() {
		if (this.shareTextFieldRef === document.activeElement) {
			this.shareTextFieldRef.setSelectionRange(0, this.shareTextFieldRef.value.length);
		}
	}

	/**
	 * @param {Event} e
	 */
	onShowTooltipButton(e) {
		e.preventDefault();
		this.setState({ tooltipVisible: true });
		copyTextToClipboard(this.props.videoUrl || window.location.href);

		clearTimeout(this.tooltipHandle);
		this.tooltipHandle = setTimeout(() => {
			this.setState({ tooltipVisible: false });
			// wait for animation
			this.tooltipHandle = setTimeout(() => {
				this.props.copyLinkTooltipChanged(false);
			}, HIDE_TOOLTIP_ANIMATION_DURATION_MSECS);
		}, MSECS_TO_HIDE_TOOLTIP);
		this.props.copyLinkTooltipChanged(true);
	}

	render() {
		let shareUrl;

		if (this.state.urlWithTimestamp) {
			shareUrl = this.generateUrlWithTimestamp();
		} else {
			shareUrl = this.props.videoUrl;
		}

		let checkField = null;
		let timeLabel = null;
		let timeTextField = null;

		if (this.props.isSeekableMedia) {
			checkField = <input type="checkbox" onChange={this.onChangeCheckbox} className="social-share-bar__checkbox" id="socialShareCheckBox" />;

			timeLabel = (
				<label htmlFor="socialShareTimeTextField" className="social-share-bar__label videoTime">
					{Translations.get('SocialShareStartAt')}&nbsp;
				</label>
			);

			timeTextField = (
				<span id="socialShareTimeTextField" className="social-share-bar__textarea videotime">
					&nbsp;{this.state.currentVideoTime}&nbsp;
				</span>
			);
		}

		const { showCopyLinkButton, forceHideTooltip } = this.props;
		const { tooltipVisible } = this.state;

		const copyIcon = tooltipVisible ? (
			<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px">
				<path d="M382-240 154-468l57-57 171 171 367-367 57 57-424 424Z" />
			</svg>
		) : (
			<svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px">
				<path d="M360-240q-33 0-56.5-23.5T280-320v-480q0-33 23.5-56.5T360-880h360q33 0 56.5 23.5T800-800v480q0 33-23.5 56.5T720-240H360Zm0-80h360v-480H360v480ZM200-80q-33 0-56.5-23.5T120-160v-560h80v560h440v80H200Zm160-240v-480 480Z" />
			</svg>
		);

		return (
			<span className={'social-share-bar__content-url-container ' + this.props.cssModifier}>
				<input
					type="text"
					readOnly={!Flux.Browser.isSafariMobile()}
					value={shareUrl}
					className={'social-share-bar__textarea share ' + this.props.cssModifier}
					onFocus={this.selectUrlForCopy}
					ref={(shareTextFieldRef) => (this.shareTextFieldRef = shareTextFieldRef)}
				/>
				{checkField}
				{timeLabel}
				{timeTextField}
				{showCopyLinkButton && (
					<div className={`social-share-bar__content-url-copy ${tooltipVisible ? '-copied' : ''}`} onClick={this.onShowTooltipButton}>
						{copyIcon}
						<span className="social-share-bar__content-url-copy-button" onClick={this.onShowTooltipButton}>
							{Translations.get('SocialShareCopyLink')}
						</span>
						<div
							className={
								'social-share-bar__content-url-copy-button-tooltip' +
								(tooltipVisible && !forceHideTooltip ? '' : ' -hidden') +
								(forceHideTooltip ? ' -force-hidden' : '')
							}
						>
							<div className="social-share-bar__content-url-copy-button-tooltip-pointer" />
							<div className="social-share-bar__content-url-copy-button-tooltip-text">
								{Translations.get('SocialShareLinkWasCopied')}
							</div>
						</div>
					</div>
				)}
			</span>
		);
	}
}

SocialShareBarShareContentUrl.propTypes = {
	videoUrl: PropTypes.string.isRequired,
	isSeekableMedia: PropTypes.bool,
	cssModifier: PropTypes.string,
	showCopyLinkButton: PropTypes.bool,
	forceHideTooltip: PropTypes.bool,
	copyLinkTooltipChanged: PropTypes.func,
};

SocialShareBarShareContentUrl.defaultProps = {
	isSeekableMedia: true,
	cssModifier: '',
	showCopyLinkButton: false,
	forceHideTooltip: false,
	copyLinkTooltipChanged: () => {},
};

export default SocialShareBarShareContentUrl;
